import React, { useState, createRef } from 'react'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import axios from 'axios'
import ReCAPTCHA from 'react-google-recaptcha'

import {
  makeStyles,
  styled,
} from '@material-ui/core/styles'
import { spacing } from '@material-ui/system'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import globalStyles from 'src/styles/globalStyles'

import {
  Button as MuiButton,
  Container as MuiContainer,
  Hidden,
  Typography as MuiTypography,
  Grid as MuiGrid,
  Box,
  AppBar,
  Toolbar,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  MenuItem,
  MenuList,
  ButtonGroup,
  IconButton,
  TextField as MuiTextField,
  FormControl as MuiFormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  FormGroup,
  Checkbox,
  CircularProgress
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'

import SendIcon from '@material-ui/icons/Send'
import { SectionHeader } from 'src/components/headers'

import { Formik, Form } from 'formik'
import * as Yup from 'yup'

const Button = styled(MuiButton)(spacing)
const TextField = styled(MuiTextField)(spacing)
const FormControl = styled(MuiFormControl)(spacing)
const Typography = styled(MuiTypography)(spacing)
const Container = styled(MuiContainer)(spacing)
const Grid = styled(MuiGrid)(spacing)

const useStyles = makeStyles({
  ...globalStyles,
  banner: {
    overflow: 'hidden',
    position: 'absolute',
    display: 'flex',
    alignItems: 'flex-end',
    backgroundColor: '#606060',
    zIndex: '100',
    '& div': {
      opacity: '0.6',
      filter: 'blur(2px)'
    }
  },
  bannerHeader: {
    height: '500px',
    zIndex: '1000',
    position: 'relative',
    color: 'white',
  },
  boldText: {
    fontWeight: 800,
  },
  header: {
    fontWeight: 800,
    color: '#333333'
  },
  map: {
    overflow: 'hidden'
  },
  headerLine: {
    paddingTop: '5px',
    '&::before': {
      content: '""',
      display: 'block',
      width: '50px',
      height: '3px',
      position: 'relative',
      top: '-5px',
      backgroundColor: '#3871c2'
    }
  },
  fullWidth: {
    width: '100%'
  },
  noHeight: {
    height: '0%'
  },
  flexGrow: {
    flexGrow: '1'
  },
  smallSelect: {
    '& label': {
      transform: 'translate(14px, 12px) scale(1)'
    },
    '& .MuiSelect-root': {
      padding: '10.5px 14px'
    }
  },
  requirements: {
    minWidth: '300px',
    border: 'none',
    paddingLeft: '0px'
  },
  comment: {
    width: '100%',
    height: '100%'
  },
  fullWidth: {
    width: '100%'
  },
  loading: {
    color: 'rgba(0, 0, 0, 0.26)'
  },
  stepper: {
    paddingTop: '20px',
    padding: '0px'
  }
})

const defaultData = {
  person: '',
  name: '',
  taxNumber: '',
  email: '',
  phone: '',
}

const Assessment = ({ pageContext, assessment }) => {
  const Assessment = assessment
  const steps = ['Dane kontaktowe', 'Informacje transportowe']
  const classes = useStyles()
  const theme = useTheme()
  const bptMd = useMediaQuery(theme.breakpoints.up('md'))
  const bptLg = useMediaQuery(theme.breakpoints.up('lg'))

  const [data, setData] = useState(defaultData)
  const [activeStep, setActiveStep] = useState(0)
  const [recaptchaResponse, setRecaptchaResponse] = useState(null)
  const [contactSubmitError, setContactSubmitError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleNext = (values, { resetForm }) => {
    setData(values)
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }
  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1)
  const handleReset = () => setActiveStep(0)

  const requirementsList = Assessment.additionalRequirementsOptions
  const initRequirementsList = () => requirementsList.reduce((acc, curr) => (acc[curr] = false, acc), {})
  const [requirements, setRequirements] = useState(initRequirementsList())

  const toggleRequirement = (ev) => {
    setRequirements({ ...requirements, [ev.target.name]: ev.target.checked })
  }

  const msgs = {
    isRequired: Assessment.form.requiredField
  }
  const parseCharsError = (nr, msg) => msg.replace('{number}', nr)

  const ContactSchema = Yup.object().shape({
    person: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(50, parseCharsError(50, Assessment.form.maxChars)).required(msgs.isRequired),
    name: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(50, parseCharsError(50, Assessment.form.maxChars)),
    taxNumber: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(50, parseCharsError(50, Assessment.form.maxChars)),
    email: Yup.string().email(Assessment.form.invalidEmail).required(msgs.isRequired),
    phone: Yup.string().min(9, Assessment.form.invalidPhone).max(20, Assessment.form.invalidPhone).required(msgs.isRequired),
  })

  const ShippmentSchema = Yup.object().shape({
    truckType: Yup.string().required(msgs.isRequired),
    loadingFrom: Yup.string().required(msgs.isRequired),
    typeOfGoods: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(50, parseCharsError(50, Assessment.form.maxChars)),
    fromCountry: Yup.string().required(msgs.isRequired),
    fromPostal: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(20, parseCharsError(20, Assessment.form.maxChars)).required(msgs.isRequired),
    fromCity: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(50, parseCharsError(50, Assessment.form.maxChars)).required(msgs.isRequired),
    toCountry: Yup.string().required(msgs.isRequired),
    toPostal: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(20, parseCharsError(20, Assessment.form.maxChars)).required(msgs.isRequired),
    toCity: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(50, parseCharsError(50, Assessment.form.maxChars)).required(msgs.isRequired),
    comment: Yup.string().min(3, parseCharsError(3, Assessment.form.minChars)).max(250, parseCharsError(250, Assessment.form.maxChars)),
  })

  const handleRecaptchaChange = (v) => setRecaptchaResponse(v)

  const afterSubmit = () => {
    setLoading(false)
    setTimeout(() => {
      setSubmitError(null)
    }, 4000)
  }
  const submitForm = (values, { resetForm }) => {
    setLoading(true)
    const payload = {
      ...data,
      ...values
    }

    const requirementsString = Object.keys(requirements).filter(x => requirements[x]).join(', ')

    axios.post('https://api.fpdrozd.com/bama/price-request', {
      ...payload,
      requirements: requirementsString,
      recaptcha: recaptchaResponse || ''
    })
    .then(res => {
      setData(defaultData)
      handleBack()
      setSubmitError(false)
      setRequirements(initRequirementsList())
      resetForm()
      afterSubmit()
    })
    .catch(err => {
      setSubmitError(true)
      afterSubmit()
    })
  }

  return (
    <Container mt={8} id={Assessment.page.path.slice(1)}>
      <Box mb={8}>
        <SectionHeader>{Assessment.title}</SectionHeader>

        <Stepper className={classes.stepper} activeStep={activeStep} orientation="vertical">
          <Step key={0}>
            <StepLabel>{Assessment.contactStep}</StepLabel>
            <StepContent>
              <Formik
                validationSchema={ContactSchema}
                onSubmit={handleNext}
                initialValues={data}
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  values,
                  touched,
                  errors,
                }) => (
                  <Form>
                    <Box pt={1}>
                      <TextField className={classes.fullWidth} value={values.person} onChange={handleChange} onBlur={handleBlur} error={touched.person && !!errors.person} helperText={touched.person && errors.person} name="person" label={Assessment.person} variant="outlined" size="small" />
                    </Box>

                    <Grid container pt={2} spacing={bptMd ? 1 : 2}>
                      <Grid item xs={12} md={6}>
                        <TextField className={classes.fullWidth} value={values.name} onChange={handleChange} onBlur={handleBlur} error={touched.name && !!errors.name} helperText={touched.name && errors.name} name="name" label={Assessment.companyName} variant="outlined" size="small" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField className={classes.fullWidth} value={values.taxNumber} onChange={handleChange} onBlur={handleBlur} error={touched.taxNumber && !!errors.taxNumber} helperText={touched.taxNumber && errors.taxNumber} name="taxNumber" label={Assessment.taxNumber} variant="outlined" size="small" />
                      </Grid>
                    </Grid>

                    <Grid container spacing={bptMd ? 1 : 2} pt={2}>
                      <Grid item xs={12} md={6}>
                        <TextField className={classes.fullWidth} value={values.email} onChange={handleChange} onBlur={handleBlur} error={touched.email && !!errors.email} helperText={touched.email && errors.email} mr={1} name="email" label={Assessment.form.email} variant="outlined" size="small" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField className={classes.fullWidth} value={values.phone} onChange={handleChange} onBlur={handleBlur} error={touched.phone && !!errors.phone} helperText={touched.phone && errors.phone} name="phone" label={Assessment.form.phone} variant="outlined" size="small" />
                      </Grid>
                    </Grid>

                    <Box display="flex" justifyContent={bptMd ? 'start' : 'center'} flexDirection={bptMd ? 'row' : 'column'} alignItems={bptMd ? 'flex-end' : 'center'} flexWrap="wrap" pt={3}>
                      <Button onClick={handleSubmit} variant="contained" disabled={!!Object.keys(errors).length} disableElevation color="primary" mt={bptMd ? 0 : 1}>
                        {Assessment.next}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </StepContent>
          </Step>
          <Step key={1}>
            <StepLabel>{Assessment.shippmentStep}</StepLabel>
            <StepContent>
              <Formik
                validationSchema={ShippmentSchema}
                onSubmit={submitForm}
                initialValues={{
                  truckType: '',
                  loadingFrom: '',
                  typeOfGoods: '',
                  fromCountry: '',
                  fromPostal: '',
                  fromCity: '',
                  toCountry: '',
                  toPostal: '',
                  toCity: '',
                  comment: '',
                }}
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  values,
                  touched,
                  errors,
                }) => (
                  <Form>
                    <Grid container spacing={bptMd ? 1 : 2} pt={2}>
                      <Grid item xs={12} md={3}>
                        <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.truckType && !!errors.truckType} variant="outlined">
                          <InputLabel id="truck-type-label">{Assessment.truckType}</InputLabel>
                          <Select
                            value={values.truckType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="truckType"
                            labelId="truck-type-label"
                            label={Assessment.truckType}
                            size="small"
                          >
                            {Assessment.truckTypeOptions.map(item => (
                              <MenuItem value={item} key={item}>{item}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{touched.truckType && errors.truckType}</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.loadingFrom && !!errors.loadingFrom} variant="outlined">
                          <InputLabel id="loading-from-label">{Assessment.loadingFrom}</InputLabel>
                          <Select
                            value={values.loadingFrom}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="loadingFrom"
                            labelId="loading-from-label"
                            label={Assessment.loadingFrom}
                            size="small"
                          >
                            {Assessment.loadingFromOptions.map(item => (
                              <MenuItem value={item} key={item}>{item}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{touched.loadingFrom && errors.loadingFrom}</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <TextField className={classes.fullWidth} value={values.typeOfGoods} onChange={handleChange} onBlur={handleBlur} error={touched.typeOfGoods && !!errors.typeOfGoods} helperText={touched.typeOfGoods && errors.typeOfGoods} name="typeOfGoods" label={Assessment.typeOfGoods} variant="outlined" size="small" />
                      </Grid>
                    </Grid>

                    <Box display="flex" flexWrap={bptLg ? 'nowrap' : 'wrap'} pb={4}>
                      <Grid className={classes.noHeight} container spacing={bptMd ? 1 : 2} pr={bptLg ? 1 : 0}>
                        <Grid item xs={12}>
                          <Typography className={`${classes.fullWidth} ${classes.header}`} mb={1} mt={3} variant="h7" component="h4">
                            {Assessment.loadingPlace}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.fromCountry && !!errors.fromCountry} variant="outlined">
                            <InputLabel id="from-country-label">{Assessment.country}</InputLabel>
                            <Select
                              value={values.fromCountry}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="fromCountry"
                              labelId="from-country-label"
                              label={Assessment.country}
                              size="small"
                            >
                              {Assessment.countryOptions.map(item => (
                                <MenuItem value={item} key={item}>{item}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>{touched.fromCountry && errors.fromCountry}</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            className={classes.fullWidth}
                            value={values.fromPostal}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.fromPostal && !!errors.fromPostal}
                            helperText={touched.fromPostal && errors.fromPostal}
                            name="fromPostal"
                            label={Assessment.postalCode}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={7}>
                          <TextField
                            className={classes.fullWidth}
                            value={values.fromCity}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.fromCity && !!errors.fromCity}
                            helperText={touched.fromCity && errors.fromCity}
                            name="fromCity"
                            label={Assessment.city}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>

                      <Grid className={classes.noHeight} container spacing={bptMd ? 1 : 2}>
                        <Grid item xs={12}>
                          <Typography className={`${classes.fullWidth} ${classes.header}`} mb={1} mt={3} variant="h7" component="h4">
                            {Assessment.deliveryPlace}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.toCountry && !!errors.toCountry} variant="outlined">
                            <InputLabel id="to-country-label">{Assessment.country}</InputLabel>
                            <Select
                              value={values.toCountry}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="toCountry"
                              labelId="to-country-label"
                              label={Assessment.country}
                              size="small"
                            >
                              {Assessment.countryOptions.map(item => (
                                <MenuItem value={item} key={item}>{item}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>{touched.toCountry && errors.toCountry}</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            className={classes.fullWidth}
                            value={values.toPostal}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.toPostal && !!errors.toPostal}
                            helperText={touched.toPostal && errors.toPostal}
                            name="toPostal"
                            label={Assessment.postalCode}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={7}>
                          <TextField
                            className={classes.fullWidth}
                            value={values.toCity}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.toCity && !!errors.toCity}
                            helperText={touched.toCity && errors.toCity}
                            name="toCity"
                            label={Assessment.city}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <FormControl className={classes.requirements} component="fieldset">
                          <FormLabel component="legend">{Assessment.additionalRequirements}</FormLabel>
                          <FormGroup>
                            {requirementsList.map(requirement => (
                              <FormControlLabel
                                key={requirement} control={<Checkbox checked={requirements[requirement]} onChange={toggleRequirement} name={requirement} color="primary" />}
                                label={requirement}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={9}>
                        <TextField
                          className={classes.comment}
                          value={values.comment}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.comment && !!errors.comment}
                          helperText={touched.comment && errors.comment}
                          name="comment"
                          label={Assessment.comment}
                          multiline
                          rows={8}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    {submitError === null || !submitError ? '' : (
                      <Box pt={2}>
                        <Alert severity={'error'}>
                          {Assessment.error}
                        </Alert>
                      </Box>
                    )}
                    <Box display="flex" justifyContent={bptMd ? 'space-between' : 'center'} flexDirection={bptMd ? 'row' : 'column'} alignItems={bptMd ? 'flex-end' : 'center'} flexWrap="wrap" pt={2}>
                      <ReCAPTCHA onChange={handleRecaptchaChange} hl={pageContext.lang} sitekey="6Ldfv1YcAAAAAEBSPSM_Du8ZOB8rRjnt2oVojso6" />
                      <Box>
                        <Button onClick={handleBack} disableElevation color="primary" mr={2} mt={bptMd ? 0 : 3}>
                          {Assessment.back}
                        </Button>
                        <Button onClick={handleSubmit} variant="contained" disabled={!!(loading || recaptchaResponse === null || Object.keys(errors).length)} disableElevation color="primary" mt={bptMd ? 0 : 3} endIcon={loading ? <CircularProgress className={classes.loading} size={20} /> : <SendIcon />}>
                          {Assessment.send}
                        </Button>
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
            </StepContent>
          </Step>
        </Stepper>
        {submitError === null || submitError ? '' : (
          <Box pt={2}>
            <Alert severity={'success'}>
              {Assessment.success}
            </Alert>
          </Box>
        )}

        {/*<Formik
          validationSchema={Schema}
          onSubmit={submitForm}
          initialValues={{
            person: '',
            name: '',
            taxNumber: '',
            email: '',
            phone: '',
            truckType: '',
            loadingFrom: '',
            typeOfGoods: '',
            fromCountry: '',
            fromPostal: '',
            fromCity: '',
            toCountry: '',
            toPostal: '',
            toCity: '',
            comment: ''
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
          }) => (
            <Form>
              <Box pt={3}>
                <TextField className={classes.fullWidth} value={values.person} onChange={handleChange} onBlur={handleBlur} error={touched.person && !!errors.person} helperText={touched.person && errors.person} name="person" label={Assessment.person} variant="outlined" size="small" />
              </Box>

              <Grid container pt={2} spacing={bptMd ? 1 : 2}>
                <Grid item xs={12} md={6}>
                  <TextField className={classes.fullWidth} value={values.name} onChange={handleChange} onBlur={handleBlur} error={touched.name && !!errors.name} helperText={touched.name && errors.name} name="name" label={Assessment.companyName} variant="outlined" size="small" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField className={classes.fullWidth} value={values.taxNumber} onChange={handleChange} onBlur={handleBlur} error={touched.taxNumber && !!errors.taxNumber} helperText={touched.taxNumber && errors.taxNumber} name="taxNumber" label={Assessment.taxNumber} variant="outlined" size="small" />
                </Grid>
              </Grid>

              <Grid container spacing={bptMd ? 1 : 2} pt={2}>
                <Grid item xs={12} md={6}>
                  <TextField className={classes.fullWidth} value={values.email} onChange={handleChange} onBlur={handleBlur} error={touched.email && !!errors.email} helperText={touched.email && errors.email} mr={1} name="email" label={Assessment.form.email} variant="outlined" size="small" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField className={classes.fullWidth} value={values.phone} onChange={handleChange} onBlur={handleBlur} error={touched.phone && !!errors.phone} helperText={touched.phone && errors.phone} name="phone" label={Assessment.form.phone} variant="outlined" size="small" />
                </Grid>
              </Grid>

              <Grid container spacing={bptMd ? 1 : 2} pt={2}>
                <Grid item xs={12} md={3}>
                  <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.truckType && !!errors.truckType} variant="outlined">
                    <InputLabel id="truck-type-label">{Assessment.truckType}</InputLabel>
                    <Select
                      value={values.truckType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="truckType"
                      labelId="truck-type-label"
                      label={Assessment.truckType}
                      size="small"
                    >
                      {Assessment.truckTypeOptions.map(item => (
                        <MenuItem value={item} key={item}>{item}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{touched.truckType && errors.truckType}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.loadingFrom && !!errors.loadingFrom} variant="outlined">
                    <InputLabel id="loading-from-label">{Assessment.loadingFrom}</InputLabel>
                    <Select
                      value={values.loadingFrom}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="loadingFrom"
                      labelId="loading-from-label"
                      label={Assessment.loadingFrom}
                      size="small"
                    >
                      {Assessment.loadingFromOptions.map(item => (
                        <MenuItem value={item} key={item}>{item}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{touched.loadingFrom && errors.loadingFrom}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField className={classes.fullWidth} value={values.typeOfGoods} onChange={handleChange} onBlur={handleBlur} error={touched.typeOfGoods && !!errors.typeOfGoods} helperText={touched.typeOfGoods && errors.typeOfGoods} name="typeOfGoods" label={Assessment.typeOfGoods} variant="outlined" size="small" />
                </Grid>
              </Grid>

              <Box display="flex" flexWrap={bptLg ? 'nowrap' : 'wrap'} pb={4}>
                <Grid className={classes.noHeight} container spacing={bptMd ? 1 : 2} pr={bptLg ? 1 : 0}>
                  <Grid item xs={12}>
                    <Typography className={`${classes.fullWidth} ${classes.header}`} mb={1} mt={3} variant="h7" component="h4">
                      {Assessment.loadingPlace}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.fromCountry && !!errors.fromCountry} variant="outlined">
                      <InputLabel id="from-country-label">{Assessment.country}</InputLabel>
                      <Select
                        value={values.fromCountry}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name="fromCountry"
                        labelId="from-country-label"
                        label={Assessment.country}
                        size="small"
                      >
                        {Assessment.countryOptions.map(item => (
                          <MenuItem value={item} key={item}>{item}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{touched.fromCountry && errors.fromCountry}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      className={classes.fullWidth}
                      value={values.fromPostal}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.fromPostal && !!errors.fromPostal}
                      helperText={touched.fromPostal && errors.fromPostal}
                      name="fromPostal"
                      label={Assessment.postalCode}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <TextField
                      className={classes.fullWidth}
                      value={values.fromCity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.fromCity && !!errors.fromCity}
                      helperText={touched.fromCity && errors.fromCity}
                      name="fromCity"
                      label={Assessment.city}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </Grid>

                <Grid className={classes.noHeight} container spacing={bptMd ? 1 : 2}>
                  <Grid item xs={12}>
                    <Typography className={`${classes.fullWidth} ${classes.header}`} mb={1} mt={3} variant="h7" component="h4">
                      {Assessment.deliveryPlace}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl className={`${classes.smallSelect} ${classes.fullWidth}`} error={touched.toCountry && !!errors.toCountry} variant="outlined">
                      <InputLabel id="to-country-label">{Assessment.country}</InputLabel>
                      <Select
                        value={values.toCountry}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name="toCountry"
                        labelId="to-country-label"
                        label={Assessment.country}
                        size="small"
                      >
                        {Assessment.countryOptions.map(item => (
                          <MenuItem value={item} key={item}>{item}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{touched.toCountry && errors.toCountry}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      className={classes.fullWidth}
                      value={values.toPostal}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.toPostal && !!errors.toPostal}
                      helperText={touched.toPostal && errors.toPostal}
                      name="toPostal"
                      label={Assessment.postalCode}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <TextField
                      className={classes.fullWidth}
                      value={values.toCity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.toCity && !!errors.toCity}
                      helperText={touched.toCity && errors.toCity}
                      name="toCity"
                      label={Assessment.city}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl className={classes.requirements} component="fieldset">
                    <FormLabel component="legend">{Assessment.additionalRequirements}</FormLabel>
                    <FormGroup>
                      {requirementsList.map(requirement => (
                        <FormControlLabel
                          key={requirement} control={<Checkbox checked={requirements[requirement]} onChange={toggleRequirement} name={requirement} color="primary" />}
                          label={requirement}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField
                    className={classes.comment}
                    value={values.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.comment && !!errors.comment}
                    helperText={touched.comment && errors.comment}
                    name="comment"
                    label={Assessment.comment}
                    multiline
                    rows={8}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
              {submitError === null ? '' : (
                <Box pt={2}>
                  <Alert severity={submitError ? 'error' : 'success'}>
                    { submitError ? Assessment.error : Assessment.success }
                  </Alert>
                </Box>
              )}
              <Box display="flex" justifyContent={bptMd ? 'space-between' : 'center'} flexDirection={bptMd ? 'row' : 'column'} alignItems={bptMd ? 'flex-end' : 'center'} flexWrap="wrap" pt={2}>
                <ReCAPTCHA onChange={handleRecaptchaChange} hl={pageContext.lang} sitekey="6Ldfv1YcAAAAAEBSPSM_Du8ZOB8rRjnt2oVojso6" />
                <Button onClick={handleSubmit} variant="contained" disabled={!!(loading || recaptchaResponse === null || Object.keys(errors).length)} disableElevation color="primary" mt={bptMd ? 0 : 3} endIcon={loading ? <CircularProgress className={classes.loading} size={20} /> : <SendIcon />}>
                  {Assessment.send}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>*/}
      </Box>
    </Container>
  )
}

export default Assessment
